/** Tuning controls — octave shift, semitone shift, and cent fine-tuning.
 *
 * Always visible in the UI, provides +/- buttons and numeric display
 * for each tuning parameter. Also includes a toggle for clean chords
 * (just intonation) mode.
 */

import { ChevronDown, ChevronUp, Music2 } from "lucide-react";
import type { TuningState } from "../lib/stateStorage.ts";

/** Props for the TuningControls component. */
export type TuningControlsProps = {
	readonly value: TuningState;
	readonly onChange: (tuning: Partial<TuningState>) => void;
};

/** A single tuning row with +/- buttons and a label. */
function TuningRow({
	label,
	value,
	min,
	max,
	unit,
	onIncrement,
	onDecrement,
}: {
	readonly label: string;
	readonly value: number;
	readonly min: number;
	readonly max: number;
	readonly unit: string;
	readonly onIncrement: () => void;
	readonly onDecrement: () => void;
}) {
	return (
		<div className="tuning-row">
			<span className="tuning-label">{label}</span>
			<div className="tuning-buttons">
				<button
					type="button"
					className="tuning-btn"
					onClick={onDecrement}
					disabled={value <= min}
					title={`Decrease ${label}`}
				>
					<ChevronDown size={12} />
				</button>
				<span className="tuning-value">
					{value > 0 ? `+${value}` : value}
					{unit}
				</span>
				<button
					type="button"
					className="tuning-btn"
					onClick={onIncrement}
					disabled={value >= max}
					title={`Increase ${label}`}
				>
					<ChevronUp size={12} />
				</button>
			</div>
		</div>
	);
}

export function TuningControls({ value, onChange }: TuningControlsProps) {
	return (
		<div className="control-group">
			<span className="control-label">
				<Music2 size={14} />
				Tuning
			</span>
			<div className="tuning-controls">
				<TuningRow
					label="Octave"
					value={value.octaveOffset}
					min={-3}
					max={3}
					unit=""
					onIncrement={() =>
						onChange({ octaveOffset: Math.min(value.octaveOffset + 1, 3) })
					}
					onDecrement={() =>
						onChange({ octaveOffset: Math.max(value.octaveOffset - 1, -3) })
					}
				/>
				<TuningRow
					label="Semi"
					value={value.semitoneOffset}
					min={-12}
					max={12}
					unit="st"
					onIncrement={() =>
						onChange({
							semitoneOffset: Math.min(value.semitoneOffset + 1, 12),
						})
					}
					onDecrement={() =>
						onChange({
							semitoneOffset: Math.max(value.semitoneOffset - 1, -12),
						})
					}
				/>
				<TuningRow
					label="Cent"
					value={value.centOffset}
					min={-100}
					max={100}
					unit="¢"
					onIncrement={() =>
						onChange({ centOffset: Math.min(value.centOffset + 1, 100) })
					}
					onDecrement={() =>
						onChange({ centOffset: Math.max(value.centOffset - 1, -100) })
					}
				/>
				<div className="tuning-row">
					<span className="tuning-label">Clean</span>
					<button
						type="button"
						className={`tuning-toggle-btn ${value.cleanChords ? "active" : ""}`}
						onClick={() => onChange({ cleanChords: !value.cleanChords })}
						title={
							value.cleanChords
								? "Clean chords enabled — notes are tuned to pure just intonation ratios"
								: "Enable clean chords — auto-tune chord notes to just intonation"
						}
					>
						{value.cleanChords ? "On" : "Off"}
					</button>
				</div>
			</div>
		</div>
	);
}
